import { useEffect, useContext, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useReactToPrint } from "react-to-print";
import dayjs from 'dayjs';

import {
    FaEdit,
    FaPrint,
    FaUser
} from 'react-icons/fa';

import Button from '../../reusable/button/button.component.jsx';
import OrderItem from './order-item/order-item.component';

import { ToastContext } from '../../../contexts/toast.context.jsx';

import { api } from '../../../config.js';
import Client from '../../../tools/client.js';
import Tools from '../../../tools/tools.js';
import { setMobileView } from '../../../tools/mobileView.js';

import {
    AccountDetailsContainer,
    AddressLastLineContainer,
    InvoiceContainer,
    InvoiceAddressesContainer,
    InvoiceAddressContainer,
    InvoiceHeaderContainer,
    InvoiceText,
    InvoiceTotalContainer,
    InvoiceTotalItemContainer,
    InvoiceSubtitle,
    PrintContainer
} from './order-display.styles';

import {
    ColumnContainer,
    ContentContainer,
    MainContainer,
    Text,
    Table,
    TableBody,
    TableHead,
    TableHeader,
    TableRow,
    RowContainer
} from '../../../styles/component.styles.jsx';

const client = new Client();
const tools = new Tools();
const iconSize = setMobileView() ? '18px' : '28px';

const OrderDisplay = ({ user, order, products, showUpdate }) => {
    const navigate = useNavigate();
    const contentRef = useRef(null);
    const reactToPrintFn = useReactToPrint({ contentRef });
    const { successToast } = useContext(ToastContext);

    const isReship = order?.type === 'reship';

    const handleCopy = (text, successMessage) => {
        if (!text) return;
        navigator.clipboard.writeText(text);
        successToast(successMessage);
    };

    const handleReshipAction = async (action) => {
        const confirmed = window.confirm(`Are you sure you want to ${action} this reship?`);
        if (!confirmed) return;

        try {
            const res = await client.reshipDecision({action});

            console.log('Handle reship action res: ', res);

            successToast(`Reship ${action}ed successfully.`);
            window.location.reload(); // or trigger parent state refresh
        } catch (err) {
            console.error(err);
            successToast(`Failed to ${action} reship.`);
        }
    };

    if (!user || !order || !products) {
        return (
            <MainContainer>
                <Text>Loading order data...</Text>
            </MainContainer>
        );
    }

    return (
        <MainContainer>
            <>
                <PrintContainer>
                    <FaUser onClick={() => navigate(`/accounts/${user.id}`)} style={{ fontSize: iconSize, marginRight: '20px' }} alt='Go To User Account' />
                    <FaEdit onClick={() => showUpdate(true)} style={{ fontSize: iconSize, marginRight: '20px' }} />
                    <FaPrint onClick={reactToPrintFn} style={{ fontSize: iconSize }} />
                </PrintContainer>
                <AccountDetailsContainer>
                    <InvoiceSubtitle name='name' onClick={() => handleCopy(`${user.firstName} ${user.lastName}`, 'Copied Name to clipboard')}>
                        {user.firstName} {user.lastName}
                    </InvoiceSubtitle>
                    <InvoiceSubtitle name='email' onClick={() => handleCopy(user.email, 'Copied Email to clipboard')}>
                        {user.email}
                    </InvoiceSubtitle>
                    <InvoiceSubtitle name='phone' onClick={() => handleCopy(user.phone, 'Copied Phone to clipboard')}>
                        {user.phone}
                    </InvoiceSubtitle>
                </AccountDetailsContainer>
                <InvoiceContainer ref={contentRef}>
                    <InvoiceHeaderContainer>
                        <ColumnContainer>
                            <InvoiceAddressContainer>
                                <InvoiceSubtitle>{dayjs(order.createdAt).format('MM/DD/YY')}</InvoiceSubtitle>
                                <InvoiceSubtitle>Status: {order.status.toUpperCase()}</InvoiceSubtitle>
                                <InvoiceSubtitle>Type: {order.type}</InvoiceSubtitle>
                                <InvoiceSubtitle>Reference ID: {order.refId}</InvoiceSubtitle>
                                <InvoiceSubtitle>Payment Type: {order.paymentType ? order.paymentType : 'Card'}</InvoiceSubtitle>
                            </InvoiceAddressContainer>
                            <InvoiceAddressContainer />
                        </ColumnContainer>
                        <InvoiceAddressesContainer>
                            <InvoiceAddressContainer
                                cursor={'pointer'}
                                onClick={() => handleCopy(tools.formatAddressForClipboard(order.billingAddress), 'Copied Billing Address to Clipboard')}
                            >
                                <InvoiceSubtitle>Billing</InvoiceSubtitle>
                                <InvoiceText>{`${order.billingAddress?.firstName || ''} ${order.billingAddress?.lastName || ''}`}</InvoiceText>
                                <InvoiceText>{order.billingAddress?.addressOne}</InvoiceText>
                                <InvoiceText>{order.billingAddress?.addressTwo}</InvoiceText>
                                <AddressLastLineContainer>
                                    <InvoiceText>{order.billingAddress?.city}</InvoiceText>
                                    <InvoiceText>{order.billingAddress?.state}</InvoiceText>
                                    <InvoiceText>{order.billingAddress?.zipCode}</InvoiceText>
                                </AddressLastLineContainer>
                            </InvoiceAddressContainer>
                            <InvoiceAddressContainer
                                cursor={'pointer'}
                                onClick={() => handleCopy(tools.formatAddressForClipboard(order.shippingAddress), 'Copied Shipping Address to Clipboard')}
                            >
                                <InvoiceSubtitle>Shipping</InvoiceSubtitle>
                                <InvoiceText>{`${order.shippingAddress?.firstName || ''} ${order.shippingAddress?.lastName || ''}`}</InvoiceText>
                                <InvoiceText>{order.shippingAddress?.addressOne}</InvoiceText>
                                <InvoiceText>{order.shippingAddress?.addressTwo}</InvoiceText>
                                <AddressLastLineContainer>
                                    <InvoiceText>{order.shippingAddress?.city}</InvoiceText>
                                    <InvoiceText>{order.shippingAddress?.state}</InvoiceText>
                                    <InvoiceText>{order.shippingAddress?.zipCode}</InvoiceText>
                                </AddressLastLineContainer>
                            </InvoiceAddressContainer>
                        </InvoiceAddressesContainer>
                    </InvoiceHeaderContainer>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Product</TableHead>
                                <TableHead>Inventory Size</TableHead>
                                <TableHead>Quantity</TableHead>
                                <TableHead>Price</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {order.OrderItems?.map((item, index) => (
                                <OrderItem key={index} item={item} />
                            ))}
                        </TableBody>
                    </Table>
                    <InvoiceTotalContainer>
                        <InvoiceTotalItemContainer>
                            <InvoiceText>Subtotal</InvoiceText>
                            <InvoiceText>{tools.formatPrice(order.subtotal)}</InvoiceText>
                        </InvoiceTotalItemContainer>
                        {order.deliveryInsurance &&
                            <InvoiceTotalItemContainer>
                                <InvoiceText>Delivery Insurance</InvoiceText>
                                <InvoiceText>{tools.formatPrice(order.deliveryInsuranceTotal)}</InvoiceText>
                            </InvoiceTotalItemContainer>
                        }
                        <InvoiceTotalItemContainer>
                            <InvoiceText>Shipping</InvoiceText>
                            <InvoiceText>{tools.formatPrice(order.shippingTotal)}</InvoiceText>
                        </InvoiceTotalItemContainer>
                        {order.saleId &&
                            <InvoiceTotalItemContainer>
                                <InvoiceText>Sale</InvoiceText>
                                <InvoiceText>{order.Sale?.type || 'N/A'}</InvoiceText>
                            </InvoiceTotalItemContainer>
                        }
                        {order.credit &&
                            <InvoiceTotalItemContainer>
                                <InvoiceText>Credit</InvoiceText>
                                <InvoiceText>{`- $${(order.credit.credit / 100).toFixed(2)}`}</InvoiceText>
                            </InvoiceTotalItemContainer>
                        }
                        <InvoiceTotalItemContainer borderBottom={''}>
                            <InvoiceText>Total</InvoiceText>
                            <InvoiceText>{tools.formatPrice(order.total)}</InvoiceText>
                        </InvoiceTotalItemContainer>
                    </InvoiceTotalContainer>
                </InvoiceContainer>
                {(order.notes && !isReship) && (
                    <ContentContainer>
                        <InvoiceSubtitle>Customer Note</InvoiceSubtitle>
                        <InvoiceText>{order.notes}</InvoiceText>
                    </ContentContainer>
                )}
                {isReship && order.status === 'pending' && (
                    <ContentContainer>
                        <InvoiceSubtitle>Reship Request</InvoiceSubtitle>

                        {order.Images?.length > 0 && (
                            <>
                                <InvoiceText>Attached Image:</InvoiceText>
                                <img
                                    src={`${api}${order.Images[0].path}`}
                                    alt="Reship Evidence"
                                    style={{ maxWidth: '300px', marginBottom: '10px' }}
                                />
                            </>
                        )}

                        <InvoiceText><strong>Reason:</strong> {order.notes || 'No reason provided.'}</InvoiceText>

                        <RowContainer style={{ marginTop: '10px' }}>
                            <Button onClick={() => handleReshipAction('approve')} backgroundColor="green">
                                Approve Reship
                            </Button>
                            <Button onClick={() => handleReshipAction('deny')} backgroundColor="red">
                                Deny Reship
                            </Button>
                        </RowContainer>
                    </ContentContainer>
                )}
                {isReship && order.metadata?.reshipRejectionNote && (
                    <ContentContainer>
                        <InvoiceSubtitle>Rejection Note</InvoiceSubtitle>
                        <InvoiceText>{order.metadata.reshipRejectionNote}</InvoiceText>
                    </ContentContainer>
                )}
            </>
        </MainContainer>
    );
};

export default OrderDisplay;